import React from "react";
import {
  Button,
  IconButton,
  Link,
  TableCell,
  TableRow,
  TextField,
  Typography,
  Chip,
  Stack,
  Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import type { SelectedProduct } from "./SelectedProducts.types";
import { getForecastMethodEntry } from "./ForecastMethodConfig";
import { freshnessOf } from "./forecast-freshness";

// Money format: xxx,xxx.xx (no currency sign)
const fmtMoney = (val: number) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val);
// Integer format for quantities: 1,234
const fmtInt = (val: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
    Math.floor(Math.max(0, Number(val) || 0)),
  );

export type RowItemProps = {
  product: SelectedProduct;
  activeStep: number;
  displayCant: number;
  getDisplayText: (
    text: string | { en_US: string; es_MX: string } | null,
  ) => string;
  // Forecast method chip (HIST, PROM, MIX, N/D)
  forecastMethod?: string | undefined;
  // Cantidad canónica C3 (baseSuggested): ya incluye muestras y pack.
  suggestedQty?: number | undefined;
  // Samples from distribution system (pending + approved). Bloque B.4:
  // ahora suma `seeding + reposicion`. Para mostrar sólo seeding en
  // "Muestras nuevas", usar `newSamplesQty`.
  sampleQty?: number | undefined;
  // Bloque B.7: cantidad de muestras-nuevas (subset seeding) — alimenta la
  // columna "Muestras nuevas" del dialog. Si no se pasa, la columna mostrará 0.
  newSamplesQty?: number | undefined;
  onSampleClick?: (() => void) | undefined;
  onOpenDetails?: (() => void) | undefined;
  onProductClick?: (() => void) | undefined;
  // Step 3 inline edit for suggestions
  step5Editable?: boolean | undefined;
  onEditSuggested?: ((val: number | null) => void) | undefined;
  // Step 3 display-only: last purchase unit cost
  unitCost?: number | null | undefined;
  // Step 4 display-only: selling price and flag to show cost/margin
  sellingPrice?: number | null | undefined;
  step6ShowCostsAndMargin?: boolean | undefined;
  // Step 3 highlight and per-row details for auto-adjust
  highlightAdjusted?: boolean | undefined;
  // Paso 4: info para productos solo en Odoo 16
  onShowOdoo16Info?: (() => void) | undefined;
  // Inline edit for pack size (purchase_cant_user)
  onEditPack?: ((val: number | null) => void) | undefined;
  // FASE 2: Removed monthlySales prop - month columns no longer shown
  // Deprecated props kept for compatibility (ignored)
  suggestedPlusQty?: number | undefined;
  onEditSuggestedPlus?: ((val: number | null) => void) | undefined;
  highlightSampleRow?: boolean | undefined;
  // Deselecciona el producto desde el dialog (papelera por fila)
  onRemove?: (() => void) | undefined;
  // Frescura por producto (CR-8): ISO de product_mapping.updated_at del
  // último forecast. Si supera 24 h, la fila muestra el icono de aviso.
  forecastCalculatedAt?: string | null | undefined;
};

export const SelectedProductsRowItem = React.memo((props: RowItemProps) => {
  const {
    product: p,
    activeStep: _activeStep,
    displayCant,
    getDisplayText,
    suggestedQty,
    sampleQty,
    onOpenDetails,
    step5Editable,
    onEditSuggested,
    unitCost,
    sellingPrice,
    step6ShowCostsAndMargin,
    onShowOdoo16Info,
  } = props;
  const rowFreshness = React.useMemo(
    () => freshnessOf(props.forecastCalculatedAt),
    [props.forecastCalculatedAt],
  );
  // Effective qty for PO — includes demand + samples
  const effectiveQtyStep6 = React.useMemo(() => {
    const val = Number(props.suggestedPlusQty ?? suggestedQty);
    return Math.max(0, isNaN(val) ? 0 : val);
  }, [suggestedQty, props.suggestedPlusQty]);
  // Step 5 inline edit local state for click-to-edit UX
  const [editSug, setEditSug] = React.useState(false);
  const [localSug, setLocalSug] = React.useState<string>("");

  const commitSug = React.useCallback(() => {
    if (!onEditSuggested) {
      setEditSug(false);
      return;
    }
    const v = localSug.trim();
    if (v === "") onEditSuggested(null);
    else {
      const n = Math.floor(Number(v));
      onEditSuggested(isNaN(n) ? null : Math.max(0, Math.min(1000000, n)));
    }
    setEditSug(false);
  }, [localSug, onEditSuggested]);

  const cancelSug = React.useCallback(() => {
    setEditSug(false);
  }, []);

  // Pack size inline edit (click-to-edit)
  const [editPack, setEditPack] = React.useState(false);
  const [localPack, setLocalPack] = React.useState<string>("");
  const commitPack = React.useCallback(() => {
    if (!props.onEditPack) { setEditPack(false); return; }
    const v = localPack.trim();
    if (v === "") props.onEditPack(null);
    else {
      const n = Math.floor(Number(v));
      props.onEditPack(isNaN(n) || n < 1 ? null : Math.min(100000, n));
    }
    setEditPack(false);
  }, [localPack, props.onEditPack]);

  return (
    <TableRow
      key={p.unifiedProductId}
      hover
      sx={{
        cursor: "default",
        transition: "none",
        "&:hover": { transition: "none" },
        // FASE 2: highlight adjusted rows in Step 0 (Forecast de Compra)
        ...(props.highlightAdjusted
          ? { backgroundColor: (theme) => theme.palette.action.hover }
          : {}),
        // Horizon-added: subtle tinted background
        ...(p.addedByHorizon
          ? { backgroundColor: (theme) => `${theme.palette.info.main}14` }
          : {}),
      }}
    >
      {/* ERP column */}
      <TableCell align="left">
        <Stack direction="row" spacing={0.5} alignItems="center">
          {/* Always show chip 16 if product exists in Odoo16 (even if also in 18) */}
          {p.existsInOdoo16 ? (
            p.odoo16Id ? (
              <Chip
                size="small"
                label="16"
                color={p.existsInOdoo18 ? "default" : "warning"}
                variant={p.existsInOdoo18 ? "outlined" : "filled"}
                sx={{ fontWeight: 700 }}
                component="a"
                href={`https://mas-x-pieza.odoo.com/web#id=${p.odoo16Id}&model=product.product&view_type=form`} // allow-hardcode
                target="_blank"
                rel="noopener noreferrer"
                clickable
                title="Abrir en Odoo 16"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <Chip
                size="small"
                label="16"
                color={p.existsInOdoo18 ? "default" : "warning"}
                variant={p.existsInOdoo18 ? "outlined" : "filled"}
                sx={{ fontWeight: 700 }}
                onClick={(e) => {
                  if (!p.existsInOdoo18) {
                    e.stopPropagation();
                    onShowOdoo16Info && onShowOdoo16Info();
                  }
                }}
                clickable={Boolean(onShowOdoo16Info) && !p.existsInOdoo18}
              />
            )
          ) : null}
          {/* If product also exists (or was created) in Odoo18 provide link */}
          {p.existsInOdoo18 && p.odoo18Id ? (
            <Chip
              size="small"
              label="18"
              component="a"
              clickable
              href={`https://masporpieza.mx/odoo/action-447/${p.odoo18Id}`} // allow-hardcode
              target="_blank"
              rel="noopener noreferrer"
            />
          ) : p.existsInOdoo18 ? (
            <Chip size="small" label="18" />
          ) : null}
        </Stack>
      </TableCell>
      {/* Forecast method chip */}
      <TableCell align="center" sx={{ py: 0.25 }}>
        {(() => {
          const entry = getForecastMethodEntry(props.forecastMethod);
          return (
            <Tooltip title={entry.tooltip} arrow>
              <Chip
                label={entry.label}
                size="small"
                color={entry.color}
                variant="filled"
                sx={{ fontWeight: 700, fontSize: "0.7rem", height: 22 }}
              />
            </Tooltip>
          );
        })()}
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5} alignItems="center">
        {rowFreshness?.isStale && (
          <Tooltip
            title={`Forecast desactualizado (${rowFreshness.label})`}
            arrow
          >
            <WarningAmberOutlinedIcon
              color="warning"
              fontSize="small"
              data-testid={`forecast-stale-${p.unifiedProductId}`}
              aria-label={`Forecast desactualizado (${rowFreshness.label})`}
            />
          </Tooltip>
        )}
        <Tooltip
          title={
            p.addedByHorizon
              ? "Este producto entró al forecast por ampliar el horizonte de compra."
              : ""
          }
          arrow
          disableHoverListener={!p.addedByHorizon}
        >
          {props.onProductClick ? (
            <Link
              component="button"
              variant="body2"
              underline="hover"
              onClick={(e) => {
                e.stopPropagation();
                props.onProductClick!();
              }}
              sx={{ fontWeight: 500, textAlign: "left", cursor: "pointer" }}
            >
              {getDisplayText(p.unifiedName)}
            </Link>
          ) : (
            <Typography variant="body2" fontWeight={500}>
              {getDisplayText(p.unifiedName)}
            </Typography>
          )}
        </Tooltip>
        </Stack>
      </TableCell>
      <TableCell align="right">
        {editPack ? (
          <TextField
            size="small"
            type="number"
            value={localPack}
            onChange={(e) => { e.stopPropagation(); setLocalPack(e.target.value); }}
            onBlur={commitPack}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitPack();
              if (e.key === "Escape") setEditPack(false);
            }}
            onClick={(e) => e.stopPropagation()}
            inputProps={{ min: 1, max: 100000, style: { textAlign: "right", fontFamily: "monospace", width: 48 } }}
            autoFocus
          />
        ) : props.onEditPack ? (
          <Tooltip title="Click para editar tamaño de paquete" arrow>
            <Button
              size="small"
              variant="text"
              onClick={(e) => { e.stopPropagation(); setLocalPack(String(displayCant)); setEditPack(true); }}
              disableRipple
              sx={{ fontFamily: "monospace", whiteSpace: "nowrap", minWidth: 0 }}
            >
              {fmtInt(displayCant)}
            </Button>
          </Tooltip>
        ) : (
          <Typography variant="body2" sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
            {fmtInt(displayCant)}
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">
        <Typography
          variant="body2"
          sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}
        >
          {fmtInt(p.totalAvailableQty ?? 0)}
        </Typography>
      </TableCell>
      {/* FASE 2: Removed monthly sales columns - no longer needed */}
      {typeof suggestedQty !== "undefined" && (
        <TableCell align="right">
          {(() => {
            // Bloque C.6 — tooltip con `lost_sales_at_peak` (MXN). Si peak
            // está disponible, mostramos el "shortfall si vendieras como peak
            // y el stock + Reposición no alcanza" para guiar al comprador.
            const peak = p.peakMonthlySales;
            const lost = p.lostSalesAtPeak;
            const currency = p.lostSalesCurrency || "MXN";
            const showLostTooltip =
              typeof peak === "number" &&
              peak > 0 &&
              typeof lost === "number" &&
              lost > 0;
            const fmtMXN = (n: number): string =>
              new Intl.NumberFormat("es-MX", {
                style: "currency",
                currency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(Math.round(n));
            const tooltipContent = showLostTooltip
              ? `Si vendieras como peak histórico (${peak} u/mes), te faltarían ${fmtMXN(lost as number)} con la Reposición actual.`
              : "";
            const inner = step5Editable ? (
            editSug ? (
              <TextField
                size="small"
                type="number"
                value={localSug}
                onChange={(e) => {
                  e.stopPropagation();
                  setLocalSug(e.target.value);
                }}
                onBlur={commitSug}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitSug();
                  if (e.key === "Escape") cancelSug();
                }}
                onClick={(e) => e.stopPropagation()}
                inputProps={{
                  min: 0,
                  max: 1000000,
                  style: { textAlign: "right", fontFamily: "monospace" },
                }}
                autoFocus
              />
            ) : (
              <Button
                size="small"
                variant="text"
                onClick={(e) => {
                  e.stopPropagation();
                  setLocalSug(String(suggestedQty ?? 0));
                  setEditSug(true);
                }}
                disableRipple
                sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}
              >
                {fmtInt(suggestedQty as number)}
              </Button>
            )
          ) : onOpenDetails ? (
            <Button
              size="small"
              variant="text"
              onClick={(e) => {
                e.stopPropagation();
                onOpenDetails();
              }}
              disableRipple
              sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}
            >
              {fmtInt(suggestedQty as number)}
            </Button>
          ) : (
            <Typography
              variant="body2"
              sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}
            >
              {fmtInt(suggestedQty as number)}
            </Typography>
          );
            return showLostTooltip ? (
              <Tooltip title={tooltipContent}>
                <span>{inner}</span>
              </Tooltip>
            ) : (
              <>{inner}</>
            );
          })()}
        </TableCell>
      )}
      {/* "Muestras nuevas" column — Bloque B.7. Muestra el subset seeding
          (productos sin historial en la tienda). Si `newSamplesQty` no se
          pasa, hace fallback a `sampleQty` (total seeding+reposicion).
          La interacción de click sigue abriendo el detalle por tienda. */}
      {typeof sampleQty !== "undefined" && (
        <TableCell align="right">
          {(() => {
            const display = props.newSamplesQty ?? sampleQty;
            const clickable = display > 0 && Boolean(props.onSampleClick);
            return clickable ? (
              <Tooltip title="Muestras nuevas (seeding) — click para ver detalle por tienda">
                <Link
                  component="button"
                  variant="body2"
                  underline="hover"
                  onClick={(e) => {
                    e.stopPropagation();
                    props.onSampleClick!();
                  }}
                  sx={{
                    fontFamily: "monospace",
                    whiteSpace: "nowrap",
                    color: "success.main",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {fmtInt(display)}
                </Link>
              </Tooltip>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "monospace",
                  whiteSpace: "nowrap",
                  color: "text.secondary",
                  fontWeight: 400,
                }}
              >
                {fmtInt(display)}
              </Typography>
            );
          })()}
        </TableCell>
      )}
      {/* Unit cost */}
      {step6ShowCostsAndMargin && (
        <TableCell
          align="right"
          data-testid={`unit-cost-${p.unifiedProductId}`}
        >
          <Typography
            variant="body2"
            sx={{ fontSize: "0.775rem", whiteSpace: "nowrap" }}
          >
            {typeof unitCost === "number" ? `$ ${fmtMoney(unitCost)}` : "—"}
          </Typography>
        </TableCell>
      )}
      {/* Margin — uses effectiveQtyStep6 (demand + samples) */}
      {step6ShowCostsAndMargin && (
        <TableCell align="right" data-testid={`margin-${p.unifiedProductId}`}>
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}
          >
            {(() => {
              // B5.6 — margen real por producto: precio − costo (ambos canónicos
              // con IVA del backend vía B5.2), NO un ratio global único.
              const qty = effectiveQtyStep6 ?? 0;
              const cost =
                typeof unitCost === "number" ? Math.max(0, unitCost) : null;
              const price =
                typeof sellingPrice === "number"
                  ? Math.max(0, sellingPrice)
                  : null;
              if (qty <= 0 || cost === null || price === null) return "\u2014";
              const margin = qty * (price - cost);
              return `$ ${fmtMoney(margin)}`;
            })()}
          </Typography>
        </TableCell>
      )}
      {/* Total — uses effectiveQtyStep6 (demand + samples) */}
      {step6ShowCostsAndMargin && (
        <TableCell align="right" data-testid={`total-${p.unifiedProductId}`}>
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}
          >
            {typeof unitCost === "number" && effectiveQtyStep6 != null
              ? `$ ${fmtMoney(effectiveQtyStep6 * Math.max(0, unitCost))}`
              : "—"}
          </Typography>
        </TableCell>
      )}
      {/* Papelera — deselecciona el producto desde el dialog */}
      <TableCell align="center" sx={{ width: 44, px: 0.5 }}>
        {props.onRemove && (
          <Tooltip title="Quitar de la selección">
            <IconButton
              size="small"
              aria-label="Quitar producto de la selección"
              onClick={(e) => {
                e.stopPropagation();
                props.onRemove!();
              }}
              sx={{
                color: "text.secondary",
                "&:hover": { color: "error.main" },
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
});
